import React, { memo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import ErrorMessage from '@components/ErrorMessage'
import { UpdateManyMap } from '@modules/shortlist/api/shortlist'
import { SHORTLIST_STATUS_MAP } from '@modules/shortlist/constants'
import { Shortlist } from '@modules/shortlist/types'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'

import {
  DeleteIconWrapper,
  ShortlistItemName,
  ShortlistItemTextField,
  ShortlistItemWrapper,
  StyledShortlistItemsContainer,
  StyledShortlistItemsContainerOuter,
} from './index.styles'
import { updateItemInTheMap } from './utils'

interface EditShortlistsPopupProps {
  isOpen: boolean
  onCancel: () => void
  onConfirm: (data: UpdateManyMap) => void
  shouldClose: (data: UpdateManyMap) => boolean
  onDelete: (id: Shortlist['id']) => void
  shortlists: Shortlist[]
}

const ShortlistItemRow = memo(
  ({
    shortlist,
    shortlistName,
    value,
    onSwitch,
    onDelete,
    onChange,
    validationError,
    isInEditMode,
    setIsInEditMode,
    hasMultipleShortlists,
  }: {
    onDelete: EditShortlistsPopupProps['onDelete']
    onSwitch: (id: Shortlist, newStatus: boolean) => void
    onChange: (id: Shortlist, newName: string) => void
    value: boolean
    shortlistName: Shortlist['name']
    shortlist: Shortlist
    validationError?: string
    isInEditMode?: boolean
    setIsInEditMode?: (value: boolean) => void
    hasMultipleShortlists: boolean
  }) => {
    const initialName = useRef(shortlistName)
    const [animateBorder, setAnimateBorder] = useState<Nullable<boolean>>(null)
    const handleDelete = () => onDelete(shortlist.id)
    const handleSwitch = () => onSwitch(shortlist, !value)

    const handleChange = (str: string) => {
      setAnimateBorder(null)
      onChange(shortlist, str || '')
    }

    const handleSubmit = () => {
      if (validationError) {
        return
      }

      if (initialName.current?.trim() !== shortlistName?.trim() && animateBorder === null) {
        setAnimateBorder(true)
        setTimeout(() => setAnimateBorder(false), 5000)
      }

      setIsInEditMode?.(false)
    }

    const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return
      }

      handleSubmit()
    }

    return (
      <ShortlistItemWrapper>
        <DeleteIconWrapper onClick={handleDelete}>
          <DeleteIcon color="primary" />
        </DeleteIconWrapper>
        {isInEditMode ? (
          <ShortlistItemTextField
            autoFocus
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target?.value || '')}
            onBlur={handleSubmit}
            onKeyDown={handleKeyPress}
            value={shortlistName}
            error={!!validationError}
          />
        ) : (
          <ShortlistItemName onClick={() => setIsInEditMode?.(true)} className={animateBorder ? 'border-animation' : ''}>
            <Typography variant="body1">{shortlistName}</Typography>
          </ShortlistItemName>
        )}
        <Switch
          edge="end"
          size="small"
          checked={value ?? false}
          onClick={handleSwitch}
          onKeyPress={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && hasMultipleShortlists) {
              handleSwitch()
            }
          }}
          disabled={!hasMultipleShortlists}
        />
      </ShortlistItemWrapper>
    )
  },
)

const EditShortlistsPopup = (props: EditShortlistsPopupProps) => {
  const { isOpen, onConfirm, shortlists, onCancel, shouldClose, onDelete } = props
  const { hasAccessToMultipleShortlists } = useFeatureFlags()
  const [shortlistIdInEditMode, setShortlistIdInEditMode] = useState<Nullable<Shortlist['id']>>(null)

  const [validationError, setValidationError] = useState('')
  const scrollRef = useRef<Nullable<HTMLDivElement>>(null)

  const { t } = useTranslation()

  const [localShortlistUpdateData, setLocalShortlistUpdateData] = useState<UpdateManyMap>({})

  const handleSwitch = useCallback(
    (sh: Shortlist, newStatusBool: boolean) =>
      setLocalShortlistUpdateData(currStatuses =>
        updateItemInTheMap(currStatuses, {
          status: newStatusBool ? SHORTLIST_STATUS_MAP.ENABLED : SHORTLIST_STATUS_MAP.DISABLED,
          rawShortlist: sh,
        }),
      ),
    [],
  )

  const handleNameChange = useCallback(
    (sh: Shortlist, newName: string) => {
      if (newName?.length && !newName?.trim()) {
        return
      }

      const { id } = sh

      setLocalShortlistUpdateData(currStatuses =>
        updateItemInTheMap(currStatuses, {
          name: newName,
          rawShortlist: sh,
        }),
      )

      if (!newName) {
        setValidationError('You must add a name')
      } else if (newName && shortlists.some(s => s.name.toLowerCase() === newName?.toLowerCase() && s.id !== id)) {
        setValidationError('Name is already in use')
      } else {
        setValidationError('')
      }
    },
    [shortlists],
  )

  const handleConfirm = useCallback(() => {
    const data = Object.entries(localShortlistUpdateData).reduce(
      (acc, [key, sh]) => ({ ...acc, [key]: { ...sh, name: sh?.name?.trim() } }),
      {},
    )

    onConfirm(data)
  }, [localShortlistUpdateData])

  const handleCancelWithConfirm = useCallback(() => {
    const proceedWithClose = shouldClose?.(localShortlistUpdateData) ?? true

    if (proceedWithClose) {
      onCancel?.()
    }
  }, [localShortlistUpdateData])

  const displayedShortlists = !hasAccessToMultipleShortlists ? shortlists.slice(0, 1) : shortlists

  return (
    <ConfirmPopup
      isOpen={isOpen}
      width={290}
      title={t(translationKeys.EDIT_CUSTOM_LISTS)}
      text={t(translationKeys.SHOW_ON_CUSTOM_LIST_PAGE)}
      cancelBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
      confirmBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
      onCancel={onCancel}
      onBackdropClick={handleCancelWithConfirm}
      onConfirm={handleConfirm}
      confirmDisabled={!!validationError}
    >
      <StyledShortlistItemsContainerOuter>
        <StyledShortlistItemsContainer ref={scrollRef}>
          {displayedShortlists.map(sh => (
            <ShortlistItemRow
              shortlist={sh}
              shortlistName={localShortlistUpdateData?.[sh.id]?.name ?? sh.name}
              value={(localShortlistUpdateData?.[sh.id]?.status ?? sh.status) === SHORTLIST_STATUS_MAP.ENABLED}
              onSwitch={handleSwitch}
              onDelete={onDelete}
              onChange={handleNameChange}
              validationError={validationError}
              isInEditMode={shortlistIdInEditMode === sh.id}
              setIsInEditMode={value => !validationError && setShortlistIdInEditMode(value ? sh.id : null)}
              hasMultipleShortlists={hasAccessToMultipleShortlists}
            />
          ))}
        </StyledShortlistItemsContainer>
      </StyledShortlistItemsContainerOuter>
      <ErrorMessage error={validationError} />
    </ConfirmPopup>
  )
}

export default EditShortlistsPopup
